import React from 'react';
import { ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  getLinkedFromLabel,
  getOpenVideoLabel,
  getProviderConfig,
  getProviderLabel,
  getVideoDisplayTitle,
  getVideoLink,
  VIDEO_EXTERNAL_NOTE,
} from '../../utils/videoLink';

function ProviderMark({ provider, size = 'md' }) {
  const cfg = getProviderConfig(provider);
  const dim = size === 'lg' ? 'h-12 w-12 text-sm' : 'h-9 w-9 text-[10px]';
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-xl font-semibold tracking-tight shadow-sm ring-1 ring-black/5',
        dim,
        cfg.markClass
      )}
      aria-hidden
    >
      {cfg.mark}
    </span>
  );
}

function VisualPanel({ link, compact }) {
  const provider = link.video_provider || 'external';
  const hasThumbnail = Boolean(link.video_thumbnail_url);

  if (hasThumbnail) {
    return (
      <div className={cn('relative overflow-hidden', compact ? 'max-h-44' : 'max-h-52')}>
        <img
          src={link.video_thumbnail_url}
          alt=""
          loading="lazy"
          decoding="async"
          className="aspect-video w-full object-cover"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[hsl(24_14%_16%_/_0.35)] via-transparent to-transparent" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        'relative flex items-center justify-center overflow-hidden',
        'bg-gradient-to-br from-[hsl(42_30%_98%)] via-[hsl(40_22%_95%)] to-[hsl(38_18%_90%)]',
        compact ? 'min-h-[7.5rem]' : 'min-h-[9.5rem]'
      )}
      data-testid="video-link-placeholder"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.35]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 20%, hsl(43 48% 58% / 0.18), transparent 45%), radial-gradient(circle at 80% 80%, hsl(38 16% 72% / 0.2), transparent 40%)',
        }}
        aria-hidden
      />
      <ProviderMark provider={provider} size="lg" />
    </div>
  );
}

/**
 * Premium display card for a linked external video — view mode only.
 */
export function VideoLinkCard({
  entity,
  className,
  compact = false,
  showExternalNote = true,
  onEdit,
  onRemove,
  canEdit = false,
}) {
  const link = getVideoLink(entity);
  if (!link?.video_url) return null;

  const provider = link.video_provider || 'external';
  const cfg = getProviderConfig(provider);
  const title = getVideoDisplayTitle(link);
  const watchUrl = link.video_watch_url || link.video_url;
  const linkedFrom = getLinkedFromLabel(provider, link);
  const badgeLabel = link.video_provider_label || getProviderLabel(provider);

  return (
    <article
      className={cn(
        'overflow-hidden rounded-2xl border border-[hsl(38_14%_82%)] bg-[hsl(42_28%_99%)] shadow-[0_8px_28px_-18px_hsl(28_14%_18%_/_0.18)]',
        className
      )}
      data-testid="video-link-card"
    >
      <div className="relative">
        <VisualPanel link={link} compact={compact} />
        <span
          className={cn(
            'absolute left-3 top-3 rounded-full border px-2.5 py-0.5 text-[10px] font-semibold tracking-wide shadow-sm',
            cfg.badgeClass
          )}
        >
          {badgeLabel}
        </span>
      </div>

      <div className={cn('space-y-2.5', compact ? 'p-3.5' : 'p-4 sm:p-5')}>
        <div className="space-y-1">
          <h4 className="font-serif text-base font-medium leading-snug text-[hsl(24_14%_16%)] sm:text-lg">
            {title}
          </h4>
          {link.video_notes ? (
            <p className="text-sm leading-relaxed text-[hsl(32_10%_38%)]">{link.video_notes}</p>
          ) : null}
          <p className="text-xs text-[hsl(32_10%_42%)]">{linkedFrom}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <Button
            type="button"
            size="sm"
            className="h-9 rounded-full bg-[#8B7355] px-4 text-xs text-white hover:bg-[#7A6548]"
            asChild
          >
            <a href={watchUrl} target="_blank" rel="noopener noreferrer" data-testid="video-link-open-btn">
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              {getOpenVideoLabel()}
            </a>
          </Button>
          {canEdit && onEdit ? (
            <Button type="button" size="sm" variant="outline" className="h-9 rounded-full text-xs" onClick={onEdit}>
              Edit link
            </Button>
          ) : null}
          {canEdit && onRemove ? (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-9 text-xs text-red-600 hover:text-red-700"
              onClick={onRemove}
            >
              Remove
            </Button>
          ) : null}
        </div>

        {showExternalNote ? (
          <p className="text-[11px] leading-relaxed text-muted-foreground/80">{VIDEO_EXTERNAL_NOTE}</p>
        ) : null}
      </div>
    </article>
  );
}

export default VideoLinkCard;
